// ABOUTME: Dialog for recording a new payment or editing an existing one on an invoice
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
    Box, Typography, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import invoicingService from '../../services/invoicing';

const PaymentDialog = ({ open, invoiceId, balanceDue, onClose, onSuccess, existingPayment = null }) => {
    const isEdit = Boolean(existingPayment);

    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [referenceNo, setReferenceNo] = useState('');
    const [comments, setComments] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pre-fill when editing
    useEffect(() => {
        if (existingPayment) {
            setAmount(String(existingPayment.amount));
            setPaymentDate(existingPayment.payment_date);
            setPaymentMethod(existingPayment.payment_method);
            setReferenceNo(existingPayment.reference_number || '');
            setComments(existingPayment.comments || '');
        } else {
            resetForm();
        }
    }, [existingPayment, open]);

    const maxAmount = isEdit
        ? parseFloat(balanceDue || 0) + parseFloat(existingPayment?.amount || 0)
        : parseFloat(balanceDue || 0);

    const handleSubmit = async () => {
        if (!amount || parseFloat(amount) <= 0) {
            setError('Please enter a valid amount');
            return;
        }
        if (parseFloat(amount) > maxAmount) {
            setError(`Amount cannot exceed ₹${maxAmount.toFixed(2)}`);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const data = {
                amount: parseFloat(amount),
                payment_date: paymentDate,
                payment_method: paymentMethod,
                reference_number: referenceNo,
                comments,
            };

            if (isEdit) {
                await invoicingService.updatePayment(invoiceId, existingPayment.id, data);
                toast.success('Payment updated successfully');
            } else {
                await invoicingService.recordPayment(invoiceId, data);
                toast.success('Payment recorded successfully');
            }

            const remainingBalance = parseFloat(balanceDue) - parseFloat(amount);
            resetForm();
            await onSuccess(isEdit ? null : remainingBalance);
        } catch (err) {
            const d = err.response?.data;
            const msg = d?.detail || d?.error
                || (d && typeof d === 'object' ? Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ') : null)
                || `Failed to ${isEdit ? 'update' : 'record'} payment`;
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setAmount('');
        setPaymentDate(new Date().toISOString().split('T')[0]);
        setPaymentMethod('Cash');
        setReferenceNo('');
        setComments('');
        setError(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {!isEdit && (
                        <Typography variant="caption" color="textSecondary">
                            Balance Due: ₹{parseFloat(balanceDue || 0).toFixed(2)}
                        </Typography>
                    )}

                    <TextField
                        label="Amount"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        helperText={`Max: ₹${maxAmount.toFixed(2)}`}
                        fullWidth
                        disabled={loading}
                    />

                    <TextField
                        label="Payment Date"
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={loading}
                    />

                    <TextField
                        select
                        label="Payment Method"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        fullWidth
                        disabled={loading}
                    >
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                        <MenuItem value="Cheque">Cheque</MenuItem>
                        <MenuItem value="Card Payment">Card Payment</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </TextField>

                    <TextField
                        label="Reference No. (e.g., Cheque #, UTR)"
                        value={referenceNo}
                        onChange={(e) => setReferenceNo(e.target.value)}
                        fullWidth
                        disabled={loading}
                    />

                    <TextField
                        label="Comments"
                        multiline
                        rows={2}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        fullWidth
                        disabled={loading}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !amount}
                >
                    {loading ? <CircularProgress size={24} /> : (isEdit ? 'Update Payment' : 'Record Payment')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentDialog;
